import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Heart, 
  Package, 
  DollarSign, 
  BookOpen, 
  Settings,
  LogOut,
  Menu,
  X,
  UserPlus,
  CalendarPlus,
  FileText,
  Receipt,
  Briefcase
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import NewPatientForm from './forms/NewPatientForm';
import NewAppointmentForm from './forms/NewAppointmentForm';
import NewIncomeForm from './forms/NewIncomeForm';
import NewExpenseForm from './forms/NewExpenseForm';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/appointments', label: 'Queue & Appointments', icon: Calendar },
  { path: '/patients', label: 'Patients', icon: Users },
  { path: '/follow-ups', label: 'Follow-ups', icon: Heart },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/finance', label: 'Finance', icon: DollarSign },
  { path: '/receivables', label: 'Receivables', icon: Receipt },
  { path: '/services', label: 'Services Catalog', icon: Briefcase, doctorOnly: true },
  { path: '/directory', label: 'Referral Directory', icon: BookOpen },
  { path: '/settings', label: 'Settings', icon: Settings, doctorOnly: true },
];

const Layout = () => {
  const { user, logout, isDoctor } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickAction, setQuickAction] = useState(null);
  const [clinicSettings, setClinicSettings] = useState({ clinic_name: 'Clinic Care OS', clinic_logo: null });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get('/settings');
        setClinicSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings');
      }
    };
    fetchSettings();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const QuickActionButton = ({ icon: Icon, label, action }) => (
    <button
      onClick={() => setQuickAction(action)}
      className="quick-action w-full"
      data-testid={`quick-action-${action}`}
    >
      <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : 'w-64'}`}>
      {/* Logo Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {clinicSettings.clinic_logo ? (
            <img 
              src={clinicSettings.clinic_logo} 
              alt="Clinic Logo" 
              className="h-10 w-auto max-w-[120px] object-contain"
              data-testid="clinic-logo"
            />
          ) : null}
          <div className={clinicSettings.clinic_logo ? 'flex-1 min-w-0' : ''}>
            <h1 className="font-heading text-lg font-bold text-foreground leading-tight truncate">
              {clinicSettings.clinic_name || 'Clinic Care OS'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Malasakit sa Pasyente</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-b border-border space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Quick Actions
        </p>
        <QuickActionButton icon={UserPlus} label="Add Patient" action="patient" />
        <QuickActionButton icon={CalendarPlus} label="New Appointment" action="appointment" />
        <QuickActionButton icon={Receipt} label="Log Payment" action="income" />
        <QuickActionButton icon={FileText} label="Log Expense" action="expense" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-1">
          {navItems
            .filter(item => !item.doctorOnly || isDoctor)
            .map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => mobile && setMobileOpen(false)}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            ))}
        </div>
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card">
        <Sidebar />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-border bg-card px-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <Sidebar mobile />
          </SheetContent>
        </Sheet>
        <h1 className="font-heading text-lg font-bold">Clinic Care OS</h1>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Quick Action Dialogs */}
      <Dialog open={quickAction === 'patient'} onOpenChange={(open) => !open && setQuickAction(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Patient</DialogTitle>
          </DialogHeader>
          <NewPatientForm onSuccess={() => {
            setQuickAction(null);
            navigate('/patients');
          }} />
        </DialogContent>
      </Dialog>

      <Dialog open={quickAction === 'appointment'} onOpenChange={(open) => !open && setQuickAction(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Appointment</DialogTitle>
          </DialogHeader>
          <NewAppointmentForm onSuccess={() => {
            setQuickAction(null);
            navigate('/appointments');
          }} />
        </DialogContent>
      </Dialog>

      <Dialog open={quickAction === 'income'} onOpenChange={(open) => !open && setQuickAction(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Payment</DialogTitle>
          </DialogHeader>
          <NewIncomeForm onSuccess={() => {
            setQuickAction(null);
          }} />
        </DialogContent>
      </Dialog>

      <Dialog open={quickAction === 'expense'} onOpenChange={(open) => !open && setQuickAction(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Expense</DialogTitle>
          </DialogHeader>
          <NewExpenseForm onSuccess={() => {
            setQuickAction(null);
          }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
